import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import {
  InternetBgpConfig,
  P2PLegRole,
  P2P_LEG_LABELS,
  P2P_LEG_ROLES,
  PEERING_TYPES,
  withBgpField,
} from "../internetAddressing";

/**
 * The BGP neighbour of each leg (CTX-1132). The customer detail reads the
 * secondary from its own router (CTX-1201); until now the Admin Center could
 * not show or set it, only pass the loaded `bgpConfig` through untouched.
 */

export interface BgpNeighborsCardProps {
  bgpConfig: InternetBgpConfig | null;
  onUpdate: (bgpConfig: InternetBgpConfig) => void;
}

const FIELDS_BY_ROLE: Record<
  P2PLegRole,
  { neighbor: keyof InternetBgpConfig; peeringType: keyof InternetBgpConfig }
> = {
  primary: { neighbor: "neighborIpAddress", peeringType: "peeringType" },
  secondary: {
    neighbor: "neighborIpAddressSecondary",
    peeringType: "peeringTypeSecondary",
  },
};

export const BgpNeighborsCard: React.FC<BgpNeighborsCardProps> = ({
  bgpConfig,
  onUpdate,
}) => {
  const update = (field: keyof InternetBgpConfig, value: string) =>
    onUpdate(withBgpField(bgpConfig, field, value));

  return (
    <GenericCard isCollapsible={false} className="p-5">
      <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
        BGP Neighbors
      </h3>
      <div className="grid grid-cols-2 gap-4">
        {P2P_LEG_ROLES.map((role) => {
          const { neighbor, peeringType } = FIELDS_BY_ROLE[role];
          const label = P2P_LEG_LABELS[role];
          return (
            <React.Fragment key={role}>
              <GenericInput
                label={`${label} neighbor IP`}
                value={bgpConfig?.[neighbor] ?? ""}
                onChange={(value) => update(neighbor, value)}
                placeholder="Neighbor IP"
              />
              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                  {label} peering type
                </label>
                <GenericDropdown
                  items={[...PEERING_TYPES]}
                  value={bgpConfig?.[peeringType] ?? ""}
                  onSelect={(value) => update(peeringType, value)}
                  defaultLabel="Select peering type"
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </GenericCard>
  );
};
